<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import JuyangAiAPI from 'dashboard/api/juyangAi';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
  editable: { type: Boolean, default: false },
});

const emit = defineEmits(['changed']);
const { t } = useI18n();
const tools = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const actionId = ref(null);
const editingId = ref(null);
const showForm = ref(false);

const form = reactive({
  name: '',
  slug: '',
  description: '',
  endpoint_url: '',
  http_method: 'GET',
  request_headers: '',
  clear_request_headers: false,
  parameters_schema: '',
  identity_strategy: 'none',
});

const methodOptions = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
];

const identityOptions = computed(() => [
  { value: 'none', label: t('JUYANG_AI.TOOLS.IDENTITY_NONE') },
  { value: 'phone', label: t('JUYANG_AI.TOOLS.IDENTITY_PHONE') },
  { value: 'email', label: t('JUYANG_AI.TOOLS.IDENTITY_EMAIL') },
  { value: 'identifier', label: t('JUYANG_AI.TOOLS.IDENTITY_IDENTIFIER') },
]);

const defaultSchema = {
  type: 'object',
  properties: {},
  additionalProperties: false,
};

// JSON 花括号会被 vue-i18n 识别为占位符，因此技术示例直接在组件内生成。
const headersPlaceholder = JSON.stringify(
  { Authorization: 'Bearer ...' },
  null,
  2
);

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('JUYANG_AI.ERROR');

const loadTools = async () => {
  isLoading.value = true;
  try {
    const { data } = await JuyangAiAPI.getNested(props.assistantId, 'tools');
    tools.value = data;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  Object.assign(form, {
    name: '',
    slug: '',
    description: '',
    endpoint_url: '',
    http_method: 'GET',
    request_headers: '',
    clear_request_headers: false,
    parameters_schema: JSON.stringify(defaultSchema, null, 2),
    identity_strategy: 'none',
  });
  editingId.value = null;
  showForm.value = false;
};

const beginCreate = () => {
  resetForm();
  showForm.value = true;
};

const beginEdit = tool => {
  Object.assign(form, {
    name: tool.name,
    slug: tool.slug,
    description: tool.description,
    endpoint_url: tool.endpoint_url,
    http_method: tool.http_method,
    request_headers: '',
    clear_request_headers: false,
    parameters_schema: JSON.stringify(
      tool.parameters_schema || defaultSchema,
      null,
      2
    ),
    identity_strategy: tool.identity_strategy,
  });
  editingId.value = tool.id;
  showForm.value = true;
};

const formPayload = () => {
  const payload = {
    name: form.name.trim(),
    slug: form.slug.trim(),
    description: form.description.trim(),
    endpoint_url: form.endpoint_url.trim(),
    http_method: form.http_method,
    request_headers: form.request_headers.trim(),
    parameters_schema: JSON.parse(form.parameters_schema),
    identity_strategy: form.identity_strategy,
  };

  // 空输入默认保留已加密请求头，只有管理员明确勾选时才清除。
  if (editingId.value) {
    payload.clear_request_headers =
      form.clear_request_headers && !form.request_headers.trim();
  }

  return payload;
};

const saveTool = async () => {
  isSaving.value = true;
  try {
    const payload = formPayload();
    if (editingId.value) {
      await JuyangAiAPI.updateNested(
        props.assistantId,
        'tools',
        editingId.value,
        'tool',
        payload
      );
      useAlert(t('JUYANG_AI.TOOLS.UPDATE_SUCCESS'));
    } else {
      await JuyangAiAPI.createNested(
        props.assistantId,
        'tools',
        'tool',
        payload
      );
      useAlert(t('JUYANG_AI.TOOLS.CREATE_SUCCESS'));
    }
    resetForm();
    await loadTools();
    emit('changed');
  } catch (error) {
    useAlert(
      error instanceof SyntaxError
        ? t('JUYANG_AI.TOOLS.INVALID_SCHEMA')
        : errorMessage(error)
    );
  } finally {
    isSaving.value = false;
  }
};

const approveTool = async tool => {
  actionId.value = tool.id;
  try {
    await JuyangAiAPI.approveTool(props.assistantId, tool.id);
    useAlert(t('JUYANG_AI.TOOLS.APPROVE_SUCCESS'));
    await loadTools();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    actionId.value = null;
  }
};

const toggleTool = async (tool, enabled) => {
  actionId.value = tool.id;
  try {
    await JuyangAiAPI.updateNested(
      props.assistantId,
      'tools',
      tool.id,
      'tool',
      { enabled }
    );
    useAlert(t('JUYANG_AI.TOOLS.STATUS_SUCCESS'));
    await loadTools();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    actionId.value = null;
  }
};

const deleteTool = async tool => {
  if (!window.confirm(t('JUYANG_AI.TOOLS.DELETE_CONFIRM'))) return;

  actionId.value = tool.id;
  try {
    await JuyangAiAPI.deleteNested(props.assistantId, 'tools', tool.id);
    useAlert(t('JUYANG_AI.TOOLS.DELETE_SUCCESS'));
    await loadTools();
    emit('changed');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    actionId.value = null;
  }
};

watch(() => props.assistantId, loadTools, { immediate: true });
</script>

<template>
  <section class="flex flex-col gap-5">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('JUYANG_AI.TOOLS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('JUYANG_AI.TOOLS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="editable"
        icon="i-lucide-plus"
        :label="t('JUYANG_AI.TOOLS.ADD')"
        size="sm"
        @click="beginCreate"
      />
    </div>

    <form
      v-if="showForm"
      class="grid gap-4 border-y border-n-weak py-5 md:grid-cols-2"
      @submit.prevent="saveTool"
    >
      <Input v-model="form.name" :label="t('JUYANG_AI.TOOLS.NAME')" required />
      <Input
        v-model="form.slug"
        :label="t('JUYANG_AI.TOOLS.SLUG')"
        pattern="[a-z][a-z0-9_]*"
        required
      />
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.TOOLS.METHOD') }}
        <Select v-model="form.http_method" :options="methodOptions" />
      </label>
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.TOOLS.IDENTITY') }}
        <Select v-model="form.identity_strategy" :options="identityOptions" />
      </label>
      <Input
        v-model="form.endpoint_url"
        class="md:col-span-2"
        type="url"
        :label="t('JUYANG_AI.TOOLS.ENDPOINT')"
        placeholder="https://api.example.com/orders/{{order_id}}"
        required
      />
      <label
        class="flex flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2"
      >
        {{ t('JUYANG_AI.TOOLS.DESCRIPTION_LABEL') }}
        <textarea
          v-model="form.description"
          class="reset-base min-h-24 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
          required
        />
      </label>
      <div class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        <label for="juyang-ai-tool-headers">
          {{ t('JUYANG_AI.TOOLS.HEADERS') }}
        </label>
        <textarea
          id="juyang-ai-tool-headers"
          v-model="form.request_headers"
          class="reset-base min-h-40 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 font-mono text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
          :placeholder="headersPlaceholder"
        />
        <label
          v-if="editingId"
          class="mt-1 flex items-center gap-2 text-xs font-normal text-n-slate-11"
        >
          <Checkbox
            v-model="form.clear_request_headers"
            :disabled="Boolean(form.request_headers.trim())"
          />
          {{ t('JUYANG_AI.TOOLS.CLEAR_HEADERS') }}
        </label>
      </div>
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.TOOLS.PARAMETERS_SCHEMA') }}
        <textarea
          v-model="form.parameters_schema"
          class="reset-base min-h-40 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 font-mono text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
          required
        />
      </label>
      <p v-if="editingId" class="m-0 text-xs text-n-slate-10 md:col-span-2">
        {{ t('JUYANG_AI.TOOLS.EDIT_REAPPROVAL') }}
      </p>
      <div class="flex justify-end gap-2 md:col-span-2">
        <Button
          type="button"
          color="slate"
          variant="ghost"
          :label="t('JUYANG_AI.CANCEL')"
          @click="resetForm"
        />
        <Button
          type="submit"
          icon="i-lucide-save"
          :label="isSaving ? t('JUYANG_AI.SAVING') : t('JUYANG_AI.SAVE')"
          :is-loading="isSaving"
        />
      </div>
    </form>

    <div
      v-if="isLoading"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.LOADING') }}
    </div>
    <div
      v-else-if="!tools.length"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.TOOLS.EMPTY') }}
    </div>
    <ul v-else class="m-0 flex list-none flex-col divide-y divide-n-weak p-0">
      <li
        v-for="tool in tools"
        :key="tool.id"
        class="flex flex-col gap-3 py-4 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="m-0 text-sm font-medium text-n-slate-12">
              {{ tool.name }}
            </h3>
            <span
              class="rounded bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
            >
              {{ tool.http_method }}
            </span>
            <span
              class="text-xs"
              :class="
                tool.approval_status === 'approved'
                  ? 'text-n-teal-10'
                  : 'text-n-amber-10'
              "
            >
              {{
                tool.approval_status === 'approved'
                  ? t('JUYANG_AI.TOOLS.APPROVED')
                  : t('JUYANG_AI.TOOLS.DRAFT')
              }}
            </span>
          </div>
          <p class="m-0 mt-1 truncate text-sm text-n-slate-11">
            {{ tool.endpoint_url }}
          </p>
          <p class="m-0 mt-1 line-clamp-2 text-xs text-n-slate-10">
            {{ tool.description }}
          </p>
        </div>

        <div class="flex shrink-0 items-center gap-2">
          <span class="text-xs text-n-slate-11">
            {{
              tool.enabled ? t('JUYANG_AI.ENABLED') : t('JUYANG_AI.DISABLED')
            }}
          </span>
          <Switch
            :model-value="tool.enabled"
            :disabled="
              !editable ||
              tool.approval_status !== 'approved' ||
              actionId === tool.id
            "
            @change="enabled => toggleTool(tool, enabled)"
          />
          <Button
            v-if="editable && tool.approval_status === 'draft'"
            icon="i-lucide-shield-check"
            size="sm"
            :label="t('JUYANG_AI.TOOLS.APPROVE')"
            :is-loading="actionId === tool.id"
            @click="approveTool(tool)"
          />
          <Button
            v-if="editable"
            icon="i-lucide-pencil"
            color="slate"
            variant="ghost"
            size="sm"
            :title="t('JUYANG_AI.EDIT')"
            @click="beginEdit(tool)"
          />
          <Button
            v-if="editable"
            icon="i-lucide-trash-2"
            color="ruby"
            variant="ghost"
            size="sm"
            :title="t('JUYANG_AI.DELETE')"
            @click="deleteTool(tool)"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
